import React from "react";
import {
  Flex,
  Text,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Portal,
} from "@chakra-ui/react";
import { getExactTranslatedField, REQUIRED_LOCALE_CODE } from "utils/translations";

// A table cell that scales to any number of active languages: shows just
// the primary (English) name, plus a small hover badge revealing every
// other locale's name — instead of one column per language, which turns
// unusable once a few languages are added (7 languages = 7 near-duplicate
// columns). This is the standard multi-locale-CMS list pattern.
function TranslationsCell({ translations, locales, prefix, field = "name", noOfLines }) {
  // English is required on every record, so an exact match always exists —
  // no fallback needed (and none wanted: a missing locale should read as
  // empty, not silently show a different language's name).
  const primary = getExactTranslatedField(translations, REQUIRED_LOCALE_CODE, field);
  const otherLocales = locales.filter((l) => l.code !== REQUIRED_LOCALE_CODE);
  const hasOthers = otherLocales.some((l) => getExactTranslatedField(translations, l.code, field));

  return (
    <Flex align="center" gap={2}>
      {prefix}
      <Text noOfLines={noOfLines}>{primary || "—"}</Text>
      {locales.length > 1 && (
        <Popover trigger="hover" placement="top" isLazy>
          <PopoverTrigger>
            <Badge
              cursor="default"
              fontSize="0.65em"
              colorScheme={hasOthers ? "gray" : "gray"}
              variant={hasOthers ? "solid" : "outline"}
              borderRadius="full"
              px={2}
            >
              +{otherLocales.length}
            </Badge>
          </PopoverTrigger>
          {/* Popover isn't portaled by default — inside the table's scrollable
              container it can detach from its trigger and render in the
              wrong spot (e.g. up near the page header). Portal it to the
              document root so Popper positions it correctly regardless of
              ancestor overflow/scroll. */}
          <Portal>
            <PopoverContent w="240px">
              <PopoverArrow />
              <PopoverBody>
                <Flex direction="column" gap="4px">
                  {otherLocales.map((l) => (
                    <Flex key={l.code} justify="space-between" gap={3} fontSize="sm">
                      <Text color="gray.500" whiteSpace="nowrap">{l.label}</Text>
                      <Text dir={l.code === "ar" ? "rtl" : "ltr"} noOfLines={1}>
                        {getExactTranslatedField(translations, l.code, field) || "—"}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      )}
    </Flex>
  );
}

export default TranslationsCell;
